import { useEffect, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/components/utils/utils";
import { useI18n } from "@/hooks/useI18n";

export interface DataTableColumn<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  headClassName?: string;
  cellClassName?: string;
}

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  getRowId: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedRowId?: string | null;
  /** Reso in una colonna azioni in coda; il wrapper cella ferma la propagazione del click. Se assente, nessuna colonna azioni. */
  renderActions?: (row: T) => ReactNode;
  /** Header della colonna azioni (es. t('common.actions')). */
  actionsHeader?: ReactNode;
  /** Classi extra sull'header della colonna azioni (es. `w-[56px]` per larghezza fissa). */
  actionsHeadClassName?: string;
  /** Righe per pagina (default 10). */
  pageSize?: number;
  isLoading?: boolean;
  /** Nodo mostrato quando data è vuoto (non in loading). */
  emptyState?: ReactNode;
  /** classi extra sul contenitore radice. */
  className?: string;
}

/**
 * Tabella UI generica e dominio-agnostica.
 *
 * - Occupa tutta l'altezza del parent (`h-full`) e scrolla SOLO le righe:
 *   header sticky in cima all'area scroll, footer paginazione fisso e non scrollabile.
 * - Paginazione client-side (uncontrolled): i dati sono già tutti in memoria e
 *   vengono sliceati per pagina.
 *
 * Predisposizione futura al server-side: quando servirà, si aggiungerà una
 * modalità controllata OPZIONALE (`page`, `totalItems`, `onPageChange`) che salta
 * lo slicing interno e delega paginazione/conteggio al chiamante. L'attuale API
 * client-side resterà valida (le nuove prop saranno additive e opzionali), così
 * il passaggio non romperà i consumer esistenti.
 */
export function DataTable<T>({
  data,
  columns,
  getRowId,
  onRowClick,
  selectedRowId,
  renderActions,
  actionsHeader,
  actionsHeadClassName,
  pageSize = 10,
  isLoading,
  emptyState,
  className,
}: DataTableProps<T>) {
  const { t } = useI18n();
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const pageRows = data.slice(startIndex, startIndex + pageSize);

  // Clamp: quando `data` si riduce (es. filtri applicati dal parent) il numero
  // di pagine può calare e `currentPage` restare fuori range, mostrando una
  // pagina vuota. Dipendiamo da `totalPages` (non da `data`) per non resettare
  // ad ogni render.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const columnCount = columns.length + (renderActions ? 1 : 0);

  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      {/* Unico scroll-container: header sticky funziona solo se non c'è il
          wrapper overflow del componente Table di shadcn, per questo renderizziamo
          la <table> grezza usando solo le sotto-primitive. */}
      <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="w-full caption-bottom text-sm table-fixed">
          <TableHeader className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/80 [&_tr]:border-b [&_tr]:border-gray-200">
            <TableRow className="hover:bg-transparent">
              {columns.map((c) => (
                <TableHead
                  key={c.id}
                  className={cn(
                    "h-11 text-xs font-semibold uppercase tracking-wide text-gray-500",
                    c.headClassName
                  )}
                >
                  {c.header}
                </TableHead>
              ))}
              {renderActions && (
                <TableHead
                  className={cn(
                    "h-11 text-right text-xs font-semibold uppercase tracking-wide text-gray-500",
                    actionsHeadClassName
                  )}
                >
                  {actionsHeader}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columnCount} className="text-center py-16">
                  <Loader2 className="h-6 w-6 text-gray-300 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={columnCount}
                  className="text-center text-sm text-muted-foreground py-16"
                >
                  {emptyState}
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => (
                <TableRow
                  key={getRowId(row)}
                  className={cn(
                    "border-b border-gray-100 transition-colors last:border-0",
                    onRowClick && "cursor-pointer",
                    "hover:bg-gray-50",
                    selectedRowId === getRowId(row) &&
                      "bg-blue-50 hover:bg-blue-100"
                  )}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((c) => (
                    <TableCell key={c.id} className={c.cellClassName}>
                      {c.cell(row)}
                    </TableCell>
                  ))}
                  {renderActions && (
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {renderActions(row)}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </table>
      </div>

      {/* Footer paginazione: fuori dallo scroll, non scrollabile. Sempre
          visibile quando ci sono righe (anche con una sola pagina), così il
          conteggio risultati e i controlli restano ancorati in fondo alla
          tabella; prev/next si disabilitano da soli agli estremi. */}
      {!isLoading && data.length > 0 && (
        <div className="shrink-0 relative flex justify-center items-center pt-4">
          {/* Contatore ancorato a sinistra in `absolute` così non occupa spazio nel
              flow: la paginazione resta centrata sull'intera larghezza a prescindere
              dalla lunghezza del testo del conteggio. */}
          <div className="absolute left-0 text-sm text-gray-500">
            {t("dashboard.showingResults", {
              from: startIndex + 1,
              to: Math.min(startIndex + pageSize, data.length),
              total: data.length,
            })}
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className={cn(
                    "cursor-pointer",
                    currentPage === 1 && "pointer-events-none opacity-50"
                  )}
                />
              </PaginationItem>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => setCurrentPage(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer h-8 w-8 text-xs"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return (
                    <PaginationItem key={page}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }
                return null;
              })}

              <PaginationItem>
                <PaginationNext
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className={cn(
                    "cursor-pointer",
                    currentPage === totalPages && "pointer-events-none opacity-50"
                  )}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
